import type { MapData } from '@/types/map-data';
import { MAP_CLASSIC } from '@/maps/data/map-classic';
import { MAP_LABYRINTH } from '@/maps/data/map-labyrinth';
import { MAP_ARENA } from '@/maps/data/map-arena';
import { MAP_FORTRESS } from '@/maps/data/map-fortress';
import { MAP_GAUNTLET } from '@/maps/data/map-gauntlet';
import { MAP_SERPENTINE } from '@/maps/data/map-serpentine';
import { validateMapOrThrow } from '@/maps/map-validator';

const ALL_MAPS: ReadonlyArray<MapData> = [
  MAP_CLASSIC,
  MAP_LABYRINTH,
  MAP_ARENA,
  MAP_FORTRESS,
  MAP_GAUNTLET,
  MAP_SERPENTINE,
] as const;

// Validate all maps on load
let mapsValidated = false;

function ensureMapsValidated(): void {
  if (mapsValidated) {
    return;
  }
  for (const map of ALL_MAPS) {
    validateMapOrThrow(map);
  }
  mapsValidated = true;
}

export function getMapByIndex(index: number): MapData {
  ensureMapsValidated();
  const wrappedIndex = index % ALL_MAPS.length;
  const map = ALL_MAPS[wrappedIndex];
  if (!map) {
    throw new Error(`Map at index ${wrappedIndex} not found`);
  }
  return map;
}

export function getTotalMapCount(): number {
  return ALL_MAPS.length;
}

/**
 * Parse and validate a map from a raw JSON object.
 * This supports future level editor integration — an external editor
 * can export maps as JSON, and this function validates and loads them.
 *
 * Expected JSON format:
 * {
 *   "id": "my-map",
 *   "name": "My Custom Map",
 *   "width": 28,
 *   "height": 31,
 *   "tiles": [["wall","dot","empty",...], ...],
 *   "speedMultiplier": 1.0,
 *   "ghostReleasePoint": { "x": 13, "y": 12 },
 *   "pacmanSpawn": { "x": 13, "y": 23 },
 *   "tunnelPairs": [{ "entry": { "x": 0, "y": 14 }, "exit": { "x": 27, "y": 14 } }],
 *   "fruitSpawn": { "x": 13, "y": 17 }
 * }
 *
 * Valid tile types: "wall", "dot", "power_pill", "empty", "tunnel",
 *                   "ghost_base", "spawn_point", "fruit_spawn"
 */
export function loadMapFromJson(json: unknown): MapData {
  const mapData = json as MapData;
  validateMapOrThrow(mapData);
  return mapData;
}
